import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Volume2, VolumeX } from "lucide-react";

// Plays Steam HLS (.m3u8) trailers. Uses native HLS on Safari, hls.js elsewhere.
export default function HlsPlayer({ src, poster, className = "" }) {
  const videoRef = useRef(null);
  // Start muted so autoplay is allowed everywhere; user can unmute via the button.
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    let hls;

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
    } else if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(src);
      hls.attachMedia(video);
    } else {
      video.src = src;
    }

    const tryPlay = () => video.play().catch(() => {});
    video.addEventListener("loadedmetadata", tryPlay);

    return () => {
      video.removeEventListener("loadedmetadata", tryPlay);
      if (hls) hls.destroy();
    };
  }, [src]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !muted;
    video.muted = next;
    setMuted(next);
    // Ensure it keeps playing after unmuting.
    if (!next) video.play().catch(() => {});
  };

  return (
    <div className={`relative ${className}`}>
      <video
        ref={videoRef}
        poster={poster}
        controls
        autoPlay
        playsInline
        muted={muted}
        className="w-full h-full"
      />
      <button
        type="button"
        onClick={toggleMute}
        data-testid="trailer-mute-toggle"
        aria-label={muted ? "Unmute trailer" : "Mute trailer"}
        className="absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-2 rounded-full bg-black/60 hover:bg-black/80 text-white text-sm font-semibold backdrop-blur-sm transition-colors"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        <span className="hidden sm:inline">{muted ? "Tap for sound" : "Sound on"}</span>
      </button>
    </div>
  );
}
